import type { PublishedCard } from "@/cards/common/types";

import {
  DeckCardThumbnailRecord,
  getDeckCardCoverImageUrl,
  getDeckCardDisplayData,
  getDeckCardThumbnailSourceDebug,
  getDeckCardThumbnailSummary,
} from "@/cards/common/types/app-deck/deckCardThumbnail.types";

import {
  getDemoCardIdFromCard,
  resolveDeckCardExpandPurchased,
} from "@/cards/common/utils/app-deck/ownedDeckCardList.utils";

import { DeckCardKakaoKind } from "@/modules/kakao/deckCard/deckCardKakaoKind";

import { computeExpandCardLayout, GOLD_DARK } from "@/shared/constants/cardLayout";

import { useAuth } from "@/shared/stores/useAuth";

import { useCurrentTalentProfile } from "@/shared/stores/useProfile";

import CloseIcon from "@mui/icons-material/Close";

import { Box, Dialog, IconButton, useMediaQuery, useTheme } from "@mui/material";

import React, { useCallback, useEffect, useMemo, useState } from "react";

import DeckCardShareModal from "../../modal/DeckCardShareModal";

import DeckCardGuestLoginDialog from "../DeckCardGuestLoginDialog";

import DeckCardExpandBackFace from "./DeckCardExpandBackFace";

import DeckCardExpandDetailsDrawer from "./DeckCardExpandDetailsDrawer";

//import DeckCardFrontFace from "./DeckCardFrontFace";
import DeckCardFrontFace from "./DeckCardFrontFace";

import { useDeckCardExpandGestures } from "./useDeckCardExpandGestures";

import DeckCardlimitedEditionFrontFaceupdated from "@/modules/limited-edition/DeckCardlimitedEditionFrontFaceupdated";

import DeckCardLimitedEditionBackFace from "@/modules/limited-edition/DeckCardLimitedEditionBackFace";

import ImpactCardBackFace from "@/cards/impact-card/components/ImpactCardBackFace";
import { useCollectImpactCard } from "@/cards/impact-card/hooks/useCollectImpactCard";

import { getImpactCardData, isInvitedImpactPartnerViewer } from "@/cards/impact-card/utils/impactCardData";

import { isImpactCardCreator, isImpactCardType, readImpactCardCreatorTalentId } from "@/cards/impact-card/utils/impactCardType";

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Map deck/API card type → share/back-face kind.
 */
const shareKindFromCardType = (type?: string | null): DeckCardKakaoKind => {
  if (type === "Demo_Card") {
    return "demo";
  }

  if (type === "Season_Card") {
    return "season";
  }

  if (type === "Collection_Card") {
    return "collection";
  }

  if (isImpactCardType(type)) {
    return "impact";
  }

  return "single";
};

/* -------------------------------------------------------------------------- */
/* Props                                                                      */
/* -------------------------------------------------------------------------- */

type DeckCardExpandModalProps = {
  open: boolean;

  onClose: () => void;

  /**
   * Close expand without clearing the card id
   * from the URL, for example when opening details.
   */
  onDismiss?: () => void;

  card: PublishedCard | null;

  thumbnail?: DeckCardThumbnailRecord | null;

  onOpenDetails: (card: PublishedCard) => void;

  onShare?: (card: PublishedCard) => void;

  deckCardShareKind?: DeckCardKakaoKind;

  shareable?: boolean;

  /** Show the decorative border passed by the parent. */
  addBorder?: boolean;

  /**
   * Purchase record ID.
   *
   * Used by the Limited Edition QR:
   *
   * /limited-edition/verify/:purchaseId
   */
  purchaseId?: number | string | null;

  /**
   * Exact edition owned by the user.
   *
   * Example:
   *
   * 2 -> #002
   *
   * Display-only.
   */
  editionNumber?: number | string | null;
};

/* -------------------------------------------------------------------------- */
/* Toolbar                                                                    */
/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

const DeckCardExpandModal: React.FC<DeckCardExpandModalProps> = ({
  open,
  onClose,
  onDismiss,
  card,
  thumbnail,
  onOpenDetails,
  onShare,
  deckCardShareKind,
  shareable = true,
  addBorder = false,
  purchaseId,
  editionNumber,
}) => {
  /* ------------------------------------------------------------------------ */
  /* Auth / profile                                                           */
  /* ------------------------------------------------------------------------ */

  const { userInfo } = useAuth();

  const { currentProfile } = useCurrentTalentProfile();

  const isLoggedIn = Boolean(userInfo?.id);

  // Card 172 keeps its special image-only front face.
  const isCard172 = Number(card?.id) === 172  || Number(card?.id) === 166;
  // Collection is available for every Impact Card, not only card 164.
  const isImpactCardForCollection = isImpactCardType(
    thumbnail?.type ?? card?.type ?? card?.pages?.[0]?.type
  );
  const impactCollection = useCollectImpactCard({
    mainCardId: isImpactCardForCollection ? Number(card?.id) || undefined : undefined,
  });

  const isCollectionCard = thumbnail?.type === "Collection_Card";

  /* ------------------------------------------------------------------------ */
  /* Responsive                                                               */
  /* ------------------------------------------------------------------------ */

  const theme = useTheme();

  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const isCompact = useMediaQuery(theme.breakpoints.down("sm"));

  /* ------------------------------------------------------------------------ */
  /* Local state                                                              */
  /* ------------------------------------------------------------------------ */

  const [loginDialogOpen, setLoginDialogOpen] = useState(false);

  const [shareOpen, setShareOpen] = useState(false);
  const [downloadTrigger, setDownloadTrigger] = useState(0);
  const [downloadTriggerLimitedEdition, setDownloadTriggerLimitedEdition] = useState(0);
  // True while html-to-image is preparing the file
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingLimitedEdition, setIsDownloadingLimitedEdition] = useState(false);

  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== "undefined" ? window.innerWidth : 390,

    h: typeof window !== "undefined" ? window.innerHeight : 844,
  }));

  /* ------------------------------------------------------------------------ */
  /* Limited Edition detection                                                */
  /* ------------------------------------------------------------------------ */

  /**
   * This represents whether the card itself
   * is configured as a Limited Edition.
   *
   * It is NOT the user's purchased edition.
   */
  const limited_edition =
    thumbnail?.limited_edition ??
    (
      card as {
        limited_edition?: number | null;
      } | null
    )?.limited_edition;

  const isLimitedEdition =
    limited_edition !== null && limited_edition !== undefined && Number(limited_edition) > 0;

  /* ------------------------------------------------------------------------ */
  /* Purchase information                                                     */
  /* ------------------------------------------------------------------------ */

  /**
   * Purchase data can arrive from different levels
   * depending on where the expanded modal was opened.
   */
  const thumbnailPurchase = thumbnail?.purchase as
    | {
        id?: number | string | null;

        edition?: number | string | null;

        created_at?: string | null;
      }
    | null
    | undefined;

  const cardPurchase = (
    card as {
      purchase?: {
        id?: number | string | null;

        edition?: number | string | null;

        created_at?: string | null;
      } | null;

      purchase_id?: number | string | null;

      edition?: number | string | null;
    } | null
  )?.purchase;

  /**
   * Purchase ID priority:
   *
   * 1. Explicit modal prop
   * 2. thumbnail.purchase.id
   * 3. card.purchase.id
   * 4. card.purchase_id
   */
  const resolvedPurchaseId =
    purchaseId ??
    thumbnailPurchase?.id ??
    cardPurchase?.id ??
    (
      card as {
        purchase_id?: number | string | null;
      } | null
    )?.purchase_id ??
    null;

  /**
   * Exact edition owned.
   *
   * IMPORTANT:
   *
   * Do NOT fall back to `limitedEdition`.
   *
   * limitedEdition describes the card configuration.
   * editionNumber describes the actual piece owned.
   */
  const resolvedEditionNumber =
    editionNumber ??
    thumbnailPurchase?.edition ??
    cardPurchase?.edition ??
    (
      card as {
        edition?: number | string | null;
      } | null
    )?.edition ??
    null;

  const hasPurchaseId =
    resolvedPurchaseId !== null &&
    resolvedPurchaseId !== undefined &&
    String(resolvedPurchaseId).trim() !== "" &&
    Number(resolvedPurchaseId) > 0;

  /* ------------------------------------------------------------------------ */
  /* Viewport synchronization                                                 */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (!open) {
      setShareOpen(false);
      setIsDownloading(false);
      setIsDownloadingLimitedEdition(false);

      return;
    }

    const sync = () => {
      setViewport({
        w: window.innerWidth,

        h: window.innerHeight,
      });
    };

    sync();

    window.addEventListener("resize", sync);

    window.visualViewport?.addEventListener("resize", sync);

    return () => {
      window.removeEventListener("resize", sync);

      window.visualViewport?.removeEventListener("resize", sync);
    };
  }, [open]);

  /* ------------------------------------------------------------------------ */
  /* Drawer                                                                   */
  /* ------------------------------------------------------------------------ */

  const drawerMaxHeight = useMemo(() => {
    const ratio = isCompact ? 0.75 : isDesktop ? 0.58 : 0.64;

    if (isCompact) {
      return Math.round(viewport.h * ratio);
    }

    const cap = isDesktop ? 460 : 500;

    return Math.min(Math.round(viewport.h * ratio), cap);
  }, [viewport.h, isCompact, isDesktop]);

  /* ------------------------------------------------------------------------ */
  /* Open details                                                             */
  /* ------------------------------------------------------------------------ */

  const handleOpenDetails = () => {
    if (!card) {
      return;
    }

    if (!isLoggedIn && !isCollectionCard) {
      setLoginDialogOpen(true);

      return;
    }

    onOpenDetails(card);

    (onDismiss ?? onClose)();
  };

  const handleCollectImpactCard = async (): Promise<void> => {
    if (!isImpactCardForCollection || isCardOwner) {
      throw new Error("This card cannot be collected.");
    }

    if (!isLoggedIn) {
      setLoginDialogOpen(true);
      return;
    }

    if (isCardPurchased || impactCollection.justCollected || impactCollection.isCollecting) {
      return;
    }

    // Allow backend errors to propagate so the drawer never marks a failed
    // collection as successful. useCollectImpactCard handles error notifications.
    await impactCollection.collect();
  };

  /* ------------------------------------------------------------------------ */
  /* Gestures                                                                 */
  /* ------------------------------------------------------------------------ */

  const gestures = useDeckCardExpandGestures({
    open,

    drawerMaxHeight,

    isCompact,

    onCardTap: handleOpenDetails,
  });

  /* ------------------------------------------------------------------------ */
  /* Expanded card layout                                                     */
  /* ------------------------------------------------------------------------ */

  const cardLayout = useMemo(() => {
    const hintChrome = isDesktop ? 80 : 48;

    const availH = Math.max(
      180,

      viewport.h - gestures.drawerOffset - hintChrome
    );

    const availW = viewport.w - (isCompact ? 16 : 28);

    return computeExpandCardLayout({
      availWidth: availW,

      availHeight: availH,

      maxWidth: isDesktop ? 480 : 400,
    });
  }, [viewport, gestures.drawerOffset, isDesktop, isCompact]);

  /* ------------------------------------------------------------------------ */
  /* Keyboard controls                                                        */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (!open) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }

      if (event.key === "ArrowLeft") {
        event.preventDefault();

        gestures.animateRotation(0);
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();

        gestures.animateRotation(180);
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();

        gestures.toggleDrawer(true);
      }

      if (event.key === "ArrowDown") {
        event.preventDefault();

        gestures.toggleDrawer(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, gestures]);

  /* ------------------------------------------------------------------------ */
  /* Purchase / ownership                                                     */
  /* ------------------------------------------------------------------------ */

  const isCardPurchased = useMemo(
    () => resolveDeckCardExpandPurchased(card, thumbnail, deckCardShareKind),
    [card, thumbnail, deckCardShareKind]
  );

  /* ------------------------------------------------------------------------ */
  /* Share kind                                                               */
  /* ------------------------------------------------------------------------ */

  const resolvedShareKind = deckCardShareKind ?? shareKindFromCardType(thumbnail?.type);

  /* ------------------------------------------------------------------------ */
  /* Card owner                                                               */
  /* ------------------------------------------------------------------------ */

  const isCardOwner = useMemo(() => {
    if (!isLoggedIn) return false;
    const creatorTalentId =
      readImpactCardCreatorTalentId(thumbnail) ??
      readImpactCardCreatorTalentId(card as { talent_id?: number; talent?: { id?: number } });
    return isImpactCardCreator(creatorTalentId, currentProfile?.id);
  }, [isLoggedIn, thumbnail, card, currentProfile?.id]);

  /* ------------------------------------------------------------------------ */
  /* Sealed                                                                   */
  /* ------------------------------------------------------------------------ */

  const isSealed =
    String(
      (
        card as {
          card_status?: string;
        }
      )?.card_status ?? ""
    ).toLowerCase() === "sealed";

  /* ------------------------------------------------------------------------ */
  /* Share                                                                    */
  /* ------------------------------------------------------------------------ */

  const handleShare = useCallback(() => {
    if (!card) {
      return;
    }

    if (onShare) {
      onShare(card);

      return;
    }

    setShareOpen(true);
  }, [card, onShare]);

  const shareMeta = useMemo(() => {
    if (!card) {
      return null;
    }

    const displayForShare = getDeckCardDisplayData(thumbnail, card);
    const coverImageUrl = getDeckCardCoverImageUrl(thumbnail, card);
    const thumbnailSources = getDeckCardThumbnailSourceDebug(thumbnail, card);

    const talentName =
      card.talent?.username ??
      currentProfile?.username ??
      currentProfile?.view_name ??
      userInfo?.username ??
      "Creator";

    return {
      talentName,

      cardTitle: card.title ?? displayForShare.title ?? talentName,

      coverImageUrl,
      imageUrl: coverImageUrl,
      thumbnailSources,

      senderName: userInfo?.username ?? talentName,
    };
  }, [card, thumbnail, currentProfile, userInfo]);

  /* ------------------------------------------------------------------------ */
  /* Display data                                                             */
  /* ------------------------------------------------------------------------ */

  const display = useMemo(() => getDeckCardDisplayData(thumbnail, card), [thumbnail, card]);

  /* ------------------------------------------------------------------------ */
  /* No render when closed                                                    */
  /* ------------------------------------------------------------------------ */

  if (!open) {
    return null;
  }

  /* ------------------------------------------------------------------------ */
  /* Derived display values                                                   */
  /* ------------------------------------------------------------------------ */

  const talentName = display.talent?.username;

  const isImpactCard = isImpactCardType(
    thumbnail?.type ?? card?.type ?? card?.pages?.[0]?.type
  );

  const shareTitle = display.title || shareMeta?.cardTitle || "Card";

  const cardTableOfContents = card?.table_of_contents;

  const thumbnailTableOfContents = thumbnail?.table_of_contents;

  const cardContentExps = card?.content?.exps;

  const cardPageExps = card?.pages?.[0]?.content?.exps;

  const thumbnailContentExps = thumbnail?.content?.exps;

  const livingPlanTableOfContents = (
    Array.isArray(cardTableOfContents)
      ? cardTableOfContents
      : Array.isArray(thumbnailTableOfContents)
        ? thumbnailTableOfContents
        : []
  ) as Array<Record<string, unknown>>;

  const livingPlanContentExps = (
    Array.isArray(cardContentExps)
      ? cardContentExps
      : Array.isArray(cardPageExps)
        ? cardPageExps
        : Array.isArray(thumbnailContentExps)
          ? thumbnailContentExps
          : []
  ) as Array<Record<string, unknown>>;

  const impactCardData = getImpactCardData({
    ...(thumbnail?.content ?? {}),
    ...(card?.pages?.[0]?.content ?? {}),
    ...(card?.content ?? {}),
    partner: card?.partner ?? thumbnail?.partner,
    partner_id: card?.partner_id ?? thumbnail?.partner_id,
    collector_count:
      thumbnail?.collector_count ??
      card?.collector_count ??
      (thumbnail?.content as { collector_count?: number } | undefined)?.collector_count ??
      (card?.content as { collector_count?: number } | undefined)?.collector_count,
    clicked_count:
      thumbnail?.clicked_count ??
      card?.clicked_count ??
      (thumbnail?.content as { clicked_count?: number } | undefined)?.clicked_count ??
      (card?.content as { clicked_count?: number } | undefined)?.clicked_count,
    viewed_count:
      thumbnail?.viewed_count ??
      card?.viewed_count ??
      (thumbnail?.content as { viewed_count?: number } | undefined)?.viewed_count ??
      (card?.content as { viewed_count?: number } | undefined)?.viewed_count,
  });

  /* ------------------------------------------------------------------------ */
  /* Front face                                                               */
  /* ------------------------------------------------------------------------ */

  const impactViewerRole = isImpactCard
    ? isCardOwner
      ? "creator"
      : isInvitedImpactPartnerViewer(impactCardData.localPartner, [
          userInfo?.id,
          currentProfile?.id,
        ])
        ? "partner"
        : isCardPurchased
          ? "contributor"
          : null
    : null;

  const frontFace = isCard172 ? (
    <Box
      aria-label="카드 표지 — 클릭하여 카드 열기"
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        borderRadius: "12px",
        bgcolor: "#102C24",
        p: 0,
        m: 0,
        border: "none",
        outline: "none",
        boxShadow: "none",
      }}
    >
      {display.imageUrl ? (
        <Box
          component="img"
          src={display.imageUrl}
          alt=""
          draggable={false}
          sx={{
            display: "block",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center",
            border: "none",
            boxShadow: "none",
            pointerEvents: "none",
            userSelect: "none",
            WebkitUserDrag: "none",
          }}
        />
      ) : null}
    </Box>
  ) : !isLimitedEdition ? (
    <DeckCardFrontFace
      display={{
        ...display,
        typeLabel: isImpactCard ? "IMPACT CARD" : display.typeLabel,
      }}
      isSealed={isSealed}
      addBorder={true}
      viewerRole={impactViewerRole}
    />
  ) : (
    <DeckCardlimitedEditionFrontFaceupdated
      totalEdition={thumbnail?.limited_edition?.toString() ?? "0"}
      talentName={
        thumbnail?.talent?.view_name ??
        thumbnail?.talent?.username ??
        card?.talent?.view_name ??
        card?.talent?.username ??
        "TAAS"
      }
      title={display.title ?? card?.title ?? "Limited Edition"}
      imageUrl={display.imageUrl}
      editionNumber={resolvedEditionNumber}
      purchaseId={hasPurchaseId ? resolvedPurchaseId : null}
      cardType="SINGLE ALBUM CARD"
      addBorder={true}
    />
  );

  /* ------------------------------------------------------------------------ */
  /* Back face                                                                */
  /* ------------------------------------------------------------------------ */

  const thumbnailSummary = getDeckCardThumbnailSummary(thumbnail);
  const backFaceProps = {
    cardId: Number(card?.id),
    summary: {
      ...(thumbnailSummary ?? {}),
      clicked_count:
        thumbnail?.clicked_count ??
        card?.clicked_count ??
        thumbnailSummary?.clicked_count,
      viewed_count:
        thumbnail?.viewed_count ??
        card?.viewed_count ??
        thumbnailSummary?.viewed_count,
    },
    talentId: display.talent?.id ?? card?.talent?.id,
    talentName,
    talentAvatarUrl: display.talent?.profile_image,
    cardTitle: shareTitle,
    disableScroll: true,
    isLoggedIn,
    isPurchased: isCardPurchased,
    isOwner: isCardOwner,
    onSignInClick: () => setLoginDialogOpen(true),
    onPurchaseClick: handleOpenDetails,
    onShare: handleShare,
    shareKind: resolvedShareKind,
    description: display.description,
    shareable,
    tableOfContents: livingPlanTableOfContents,
    contentExps: livingPlanContentExps,
    sealTime: thumbnail?.seal_time ?? card?.seal_time ?? null,
    addBorder,
    purchaseId: hasPurchaseId ? resolvedPurchaseId : null,
    onDownloadClick: () => {
      if (isDownloading) {
        return;
      }
      setIsDownloading(true);
      setDownloadTrigger((n) => n + 1);
    },
    isDownloading,
  };

  const backFace = !isLimitedEdition ? (
    isImpactCard ? (
      <ImpactCardBackFace
        {...backFaceProps}
        {...impactCardData}
        isCollected={isCardPurchased || impactCollection.justCollected}
        collectedAt={thumbnailPurchase?.created_at ?? cardPurchase?.created_at ?? null}
        createdAt={
          thumbnail?.created_at ??
          (card as { created_at?: string | null } | undefined)?.created_at ??
          null
        }
      />
    ) : (
      <DeckCardExpandBackFace {...backFaceProps} />
    )
  ) : (
    <DeckCardLimitedEditionBackFace
      cardId={Number(card?.id)}
      summary={getDeckCardThumbnailSummary(thumbnail)}
      talentId={display.talent?.id ?? card?.talent?.id}
      talentName={talentName}
      talentAvatarUrl={display.talent?.profile_image}
      cardTitle={shareTitle}
      disableScroll
      isLoggedIn={isLoggedIn}
      isPurchased={isCardPurchased}
      isOwner={isCardOwner}
      onSignInClick={() => setLoginDialogOpen(true)}
      onPurchaseClick={handleOpenDetails}
      onShare={handleShare}
      shareKind={resolvedShareKind}
      description={display.description}
      shareable={shareable}
      tableOfContents={livingPlanTableOfContents}
      contentExps={livingPlanContentExps}
      sealTime={thumbnail?.seal_time ?? card?.seal_time ?? null}
      /**
       * LIMITED EDITION EXPANDED CARD:
       *
       * Special border always enabled.
       */
      addBorder={true}
      purchaseId={hasPurchaseId ? resolvedPurchaseId : null}
      onDownloadClick={() => {
        if (isDownloadingLimitedEdition) {
          return;
        }
        setIsDownloadingLimitedEdition(true);
        setDownloadTriggerLimitedEdition((n) => n + 1);
      }}
      isDownloading={isDownloadingLimitedEdition}
    />
  );

  /* ------------------------------------------------------------------------ */
  /* Render                                                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <>
      {/* ------------------------------------------------------------------ */}
      {/* Guest login                                                       */}
      {/* ------------------------------------------------------------------ */}

      <DeckCardGuestLoginDialog open={loginDialogOpen} onClose={() => setLoginDialogOpen(false)} />

      {/* ------------------------------------------------------------------ */}
      {/* Share dialog                                                      */}
      {/* ------------------------------------------------------------------ */}

      {card && shareMeta ? (
        <DeckCardShareModal
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          cardKind={resolvedShareKind}
          mainCardId={card.id}
          talentId={card.talent?.id ?? currentProfile?.id}
          demoCardId={resolvedShareKind === "demo" ? getDemoCardIdFromCard(card) : undefined}
          shareMeta={shareMeta}
        />
      ) : null}

      {/* Hidden front card — only used to create the download image.
          Kept off-screen so flip / backface CSS does not break the capture. */}
      {open && hasPurchaseId && !isLimitedEdition ? (
        <Box
          sx={{
            position: "fixed",
            left: -10000,
            top: 0,
            width: cardLayout.width,
            height: cardLayout.height,
            pointerEvents: "none",
          }}
        >
          <DeckCardFrontFace
            display={display}
            isSealed={isSealed}
            addBorder={true}
            viewerRole={impactViewerRole}
            purchaseId={resolvedPurchaseId}
            downloadTrigger={downloadTrigger}
            onDownloadComplete={() => setIsDownloading(false)}
          />
        </Box>
      ) : null}

      {/* Hidden limited-edition front card — used only for download.
          Kept invisible (not far off-screen) so the browser still paints it. */}
      {open && isLimitedEdition && hasPurchaseId ? (
        <Box
          sx={{
            position: "fixed",
            left: 0,
            top: 0,
            width: `${cardLayout.width}px`,
            height: `${cardLayout.height}px`,
            opacity: 0,
            pointerEvents: "none",
            zIndex: -1,
            overflow: "hidden",
          }}
        >
          <DeckCardlimitedEditionFrontFaceupdated
            totalEdition={thumbnail?.limited_edition?.toString() ?? "0"}
            talentName={
              thumbnail?.talent?.view_name ??
              thumbnail?.talent?.username ??
              card?.talent?.view_name ??
              card?.talent?.username ??
              "TAAS"
            }
            title={display.title ?? card?.title ?? "Limited Edition"}
            imageUrl={display.imageUrl}
            editionNumber={resolvedEditionNumber}
            purchaseId={resolvedPurchaseId}
            cardType="SINGLE ALBUM CARD"
            addBorder={true}
            downloadTrigger={downloadTriggerLimitedEdition}
            onDownloadComplete={() => setIsDownloadingLimitedEdition(false)}
          />
        </Box>
      ) : null}

      {/* ------------------------------------------------------------------ */}
      {/* Expand dialog                                                     */}
      {/* ------------------------------------------------------------------ */}

      <Dialog
        open={open}
        onClose={onClose}
        fullScreen
        PaperProps={{
          sx: {
            bgcolor: "transparent",

            boxShadow: "none",

            overflow: "hidden",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              bgcolor: "rgba(0, 0, 0, 0.58)",

              backdropFilter: "blur(10px)",

              WebkitBackdropFilter: "blur(10px)",
            },
          },
        }}
      >
        {!card ? null : (
          <Box
            ref={gestures.gestureLayerRef}
            onPointerDownCapture={gestures.handlePointerDown}
            onPointerMoveCapture={gestures.handlePointerMove}
            onPointerUpCapture={gestures.finishPointer}
            onPointerCancelCapture={gestures.finishPointer}
            sx={{
              position: "relative",

              width: "100%",

              height: "100%",

              touchAction: "none",

              userSelect: "none",

              overflow: "hidden",
            }}
          >
            {/* ---------------------------------------------------------- */}
            {/* Close                                                      */}
            {/* ---------------------------------------------------------- */}

            <IconButton
              onClick={onClose}
              aria-label="Close"
              sx={{
                position: "absolute",

                top: {
                  xs: 12,
                  md: 20,
                },

                right: {
                  xs: 12,
                  md: 20,
                },

                bgcolor: "rgba(0,0,0,0.45)",

                color: "#fff",

                zIndex: 4,

                "&:hover": {
                  bgcolor: "rgba(0,0,0,0.62)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* ---------------------------------------------------------- */}
            {/* Card stage                                                 */}
            {/* ---------------------------------------------------------- */}

            <Box
              sx={{
                flex: 1,

                minHeight: 0,

                width: "100%",

                height: "100%",

                display: "flex",

                alignItems: "center",

                justifyContent: "center",

                px: {
                  xs: 1,
                  sm: 2,
                },

                pt: {
                  xs: 1,
                  sm: 2,
                },

                pb: `${gestures.drawerOffset + (isDesktop ? 12 : 8)}px`,

                transition: gestures.isDragging
                  ? "none"
                  : "padding-bottom 0.45s cubic-bezier(0.22, 1, 0.36, 1)",
              }}
            >
              <Box
                ref={gestures.cardTapRef}
                sx={{
                  position: "relative",

                  zIndex: 2,

                  perspective: "1200px",

                  width: cardLayout.width,

                  height: cardLayout.height,

                  flexShrink: 0,

                  cursor: "pointer",

                  touchAction: "none",

                  "& img": {
                    pointerEvents: "none",

                    WebkitUserDrag: "none",

                    userSelect: "none",
                  },
                }}
              >
                {gestures.renderFlatFace ? (
                  gestures.settledFace === "back" ? (
                    backFace
                  ) : (
                    frontFace
                  )
                ) : (
                  <Box
                    ref={gestures.cardRotateRef}
                    sx={{
                      position: "relative",

                      width: "100%",

                      height: "100%",

                      transformStyle: "preserve-3d",

                      transform: `translateZ(0) rotateY(${gestures.rotationY}deg)`,
                    }}
                  >
                    {/* ------------------------------------------------ */}
                    {/* Front                                            */}
                    {/* ------------------------------------------------ */}

                    <Box
                      sx={{
                        position: "absolute",

                        inset: 0,

                        backfaceVisibility: "hidden",

                        WebkitBackfaceVisibility: "hidden",
                      }}
                    >
                      {frontFace}
                    </Box>

                    {/* ------------------------------------------------ */}
                    {/* Back                                             */}
                    {/* ------------------------------------------------ */}

                    <Box
                      sx={{
                        position: "absolute",

                        inset: 0,

                        transform: "rotateY(180deg)",

                        backfaceVisibility: "hidden",

                        WebkitBackfaceVisibility: "hidden",
                      }}
                    >
                      {backFace}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>

            {/* ---------------------------------------------------------- */}
            {/* Details drawer                                             */}
            {/* ---------------------------------------------------------- */}

            <Box
              ref={gestures.drawerRef}
              sx={{
                position: "absolute",

                left: 0,

                right: 0,

                bottom: 0,

                height: drawerMaxHeight,

                transform: `translateY(${drawerMaxHeight - gestures.drawerOffset}px)`,

                transition: gestures.isDragging
                  ? "none"
                  : "transform 0.45s cubic-bezier(0.22, 1, 0.36, 1)",

                zIndex: 3,

                borderTopLeftRadius: "20px",

                borderTopRightRadius: "20px",

                bgcolor: "rgba(18,14,10,0.94)",

                borderTop: `1px solid ${GOLD_DARK}`,

                boxShadow: "0 -12px 40px rgba(0,0,0,0.45)",

                px: 2.5,

                pt: 1.5,

                display: "flex",

                flexDirection: "column",
              }}
            >
              {/* Drawer handle */}

              <Box
                sx={{
                  width: 42,

                  height: 4,

                  borderRadius: "999px",

                  bgcolor: "rgba(255,255,255,0.35)",

                  mx: "auto",

                  mb: 1.5,
                }}
              />

              <Box
                data-no-card-tap
                sx={{
                  flex: 1,

                  minHeight: 0,

                  display: "flex",

                  flexDirection: "column",
                }}
              >
                <DeckCardExpandDetailsDrawer
                  onCollectImpact={handleCollectImpactCard}
                  isCollectingImpact={impactCollection.isCollecting}
                  content={thumbnail?.content}
                  cardType={thumbnail?.type ?? card?.type ?? card?.pages?.[0]?.type}
                  cardTitle={display.title}
                  talentAvatarUrl={display.talent?.profile_image}
                  talentName={talentName}
                  price={display.price ?? thumbnail?.price}
                  mainCardId={Number(card.id)}
                  productId={
                    Number(
                      thumbnail?.content?.id ?? thumbnail?.ref_id ?? card.pages?.[0]?.content?.id
                    ) || undefined
                  }
                  deckStatus={
                    thumbnail?.deck_status ??
                    (
                      card as {
                        deck_status?: boolean;
                      }
                    ).deck_status
                  }
                  sealTime={thumbnail?.seal_time ?? card.seal_time ?? null}
                  cardStatus={thumbnail?.card_status ?? card.card_status}
                  isPurchased={isCardPurchased || (isImpactCard && impactCollection.justCollected)}
                  /**
                   * Use the same resolved purchase
                   * identity used by the QR.
                   */
                  purchase={
                    hasPurchaseId
                      ? {
                          id: Number(resolvedPurchaseId),

                          created_at:
                            thumbnailPurchase?.created_at ?? cardPurchase?.created_at ?? undefined,
                        }
                      : null
                  }
                  pieceCount={
                    (card?.table_of_contents?.length ?? 0) + (card?.content?.exps?.length ?? 0)
                  }
                  isOwner={isCardOwner}
                  isLoggedIn={isLoggedIn}
                  shareKind={resolvedShareKind}
                  onOpenDetails={handleOpenDetails}
                  onRequireLogin={() => setLoginDialogOpen(true)}
                  talentId={display.talent?.id ?? card?.talent?.id}
                  impactFunds={
                    isImpactCard
                      ? {
                          impactGoal: impactCardData.impactGoal,
                          expectedOutcome: impactCardData.expectedOutcome,
                          targetAmount: impactCardData.targetAmount,
                          committedFund: impactCardData.committedFund,
                          verifiedFund: impactCardData.verifiedFund,
                          contributorCount: impactCardData.contributorCount,
                          collectorCount: impactCardData.collectorCount,
                          clickedCount: impactCardData.clickedCount,
                          viewedCount: impactCardData.viewedCount,
                          useOfFunds: impactCardData.useOfFunds,
                          beneficiaryName: impactCardData.beneficiary?.name,
                        }
                      : null
                  }
                />
              </Box>
            </Box>
          </Box>
        )}
      </Dialog>
    </>
  );
};

export default DeckCardExpandModal;
